import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Network, Plus } from 'lucide-react'

import type { Department, LedgerKey } from '@/lib/api/department-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { RowDeleteButton } from '@/components/common/row-delete-button'
import { DepartmentFormDialog } from '@/components/departments/department-form-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useAllDepartments,
    useDeleteDepartment,
} from '@/hooks/use-department-query'
import { useDebounced } from '@/hooks/use-debounced'
import { ledgerLabel } from '@/lib/ledger-labels'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/organization/departments/',
)({
    component: DepartmentsPage,
})

function DepartmentsPage() {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editing, setEditing] = useState<Department | undefined>()

    // Đọc hết cây rồi lọc tại chỗ: màn này cần cha và con nằm cạnh nhau, và
    // bảng "sổ đã có chủ" phải đủ mọi phòng ban mới khoá đúng ô chọn sổ.
    const { data, isLoading } = useAllDepartments()
    const all = useMemo(() => data ?? [], [data])
    const departments = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase()
        if (!q) return all
        return all.filter((d) => d.name.toLowerCase().includes(q))
    }, [all, debouncedSearch])
    const remove = useDeleteDepartment()

    /** Phòng ban cấp cha để chọn làm cha — cây chỉ hai cấp. */
    const parentOptions = useMemo(
        () =>
            all.filter(
                (d) => !d.parent_id && (!editing || d.id !== editing.id),
            ),
        [all, editing],
    )

    /** Sổ nào đang thuộc phòng ban nào: form khoá sổ đã có chủ khác. */
    const ledgerOwners = useMemo(() => {
        const owners = new Map<LedgerKey, Department>()
        for (const d of all) {
            for (const key of d.ledger_keys) owners.set(key, d)
        }
        return owners
    }, [all])

    const openCreate = () => {
        setEditing(undefined)
        setDialogOpen(true)
    }
    const openEdit = (d: Department) => {
        setEditing(d)
        setDialogOpen(true)
    }

    /** Vì sao dòng này không xoá được — hiện TRƯỚC khi người dùng bấm. */
    const blockedReason = (d: Department): string | undefined => {
        if (d.child_count > 0) return m.dept_blocked_children()
        if (d.employee_count > 0) return m.dept_blocked_employees()
        return undefined
    }

    return (
        <div className="space-y-4 p-4">
            <ListPageHeader
                icon={<Network className="size-6" />}
                title={m.dept_title()}
                subtitle={m.dept_subtitle()}
                primaryActions={
                    <Button onClick={openCreate}>
                        <Plus className="size-4" />
                        {m.dept_add()}
                    </Button>
                }
            />

            <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={m.dept_search_placeholder()}
                aria-label={m.dept_search_placeholder()}
                className="max-w-sm"
            />

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.dept_col_name()}</TableHead>
                            <TableHead>{m.dept_col_parent()}</TableHead>
                            <TableHead>{m.dept_col_ledgers()}</TableHead>
                            <TableHead className="text-right">
                                {m.dept_col_employees()}
                            </TableHead>
                            <TableHead className="w-24" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {departments.map((d) => (
                            <TableRow key={d.id}>
                                <TableCell className="font-medium">
                                    {d.name}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {d.parent_name || '—'}
                                </TableCell>
                                <TableCell>
                                    {d.ledger_keys.length === 0 ? (
                                        <span className="text-muted-foreground">
                                            {m.dept_no_ledger()}
                                        </span>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {d.ledger_keys.map((key) => (
                                                <Badge
                                                    key={key}
                                                    variant="secondary"
                                                >
                                                    {ledgerLabel(key)}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {d.employee_count}
                                </TableCell>
                                <TableCell>
                                    <div className="flex justify-end gap-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => openEdit(d)}
                                        >
                                            {m.common_edit()}
                                        </Button>
                                        <RowDeleteButton
                                            label={m.common_delete()}
                                            blockedReason={blockedReason(d)}
                                            confirmMessage={m.dept_delete_confirm()}
                                            pending={remove.isPending}
                                            onDelete={() => remove.mutate(d.id)}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!isLoading && departments.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.dept_empty()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <DepartmentFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                department={editing}
                parentOptions={parentOptions}
                ledgerOwners={ledgerOwners}
            />
        </div>
    )
}
